import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { MessageSquare, X, Send, Mail, FileText } from "lucide-react";
import { useSettings, DEFAULT_CONTACT } from "@/components/shared";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getSessionId = () => {
  let id = localStorage.getItem("pm_chat_session");
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem("pm_chat_session", id);
  }
  return id;
};

const WELCOME =
  "Bonjour ! Quel service souhaitez-vous contacter ? Choisissez ci-dessous : l'agent spécialisé prendra le relais et se concentrera sur votre demande.";

const SERVICE_CHOICES = [
  { key: "palais", label: "Palais Maasaï", sub: "Restaurant · Bar · Événements" },
  { key: "comptoir", label: "Le Comptoir Maasaï", sub: "Boutique · Click & Collect" },
  { key: "services", label: "Maasaï Services", sub: "Ménage · Nettoyage · Devis" },
  { key: "univers", label: "Autre question", sub: "Je vous oriente" },
];

export default function ChatWidget() {
  const settings = useSettings();
  const contact = { ...DEFAULT_CONTACT, ...(settings?.contact || {}) };
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([{ role: "assistant", text: WELCOME, agent_name: "Assistant Univers Maasaï" }]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [currentAgent, setCurrentAgent] = useState("Assistant Univers Maasaï");
  const [service, setService] = useState(null);
  const [showChoices, setShowChoices] = useState(true);
  const [memory, setMemory] = useState(true);
  const bottomRef = useRef(null);

  const chooseService = (choice) => {
    setService(choice.key);
    setShowChoices(false);
    setMessages((m) => [
      ...m,
      { role: "user", text: choice.label },
      {
        role: "assistant",
        agent_name: "Assistant Univers Maasaï",
        text: `Très bien, je me concentre sur ${choice.label} (${choice.sub.toLowerCase()}). Posez votre question ! Si je n'ai pas la réponse, je vous mettrai en lien direct avec l'équipe.`,
      },
    ]);
  };

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open]);

  const send = async (e) => {
    e.preventDefault();
    const text = input.trim();
    if (!text || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text }, { role: "assistant", text: "" }]);
    setLoading(true);
    try {
      const resp = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          session_id: getSessionId(),
          message: text,
          page: window.location.pathname,
          memory,
          agent_hint: service || "",
        }),
      });
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          if (!part.startsWith("data: ")) continue;
          const payload = part.slice(6).trim();
          if (payload === "[DONE]") continue;
          try {
            const parsed = JSON.parse(payload);
            if (parsed.agent) {
              setCurrentAgent(parsed.agent_name);
              setMessages((m) => {
                const next = [...m];
                next[next.length - 1] = { ...next[next.length - 1], agent_name: parsed.agent_name };
                return next;
              });
            } else if (parsed.payment_link) {
              setMessages((m) => [
                ...m,
                {
                  role: "assistant",
                  text: "Votre réservation est enregistrée. Pour la confirmer, réglez l'acompte via le lien sécurisé ci-dessous :",
                  link: parsed.payment_link,
                  linkLabel: `Payer l'acompte — ${String(parsed.deposit).replace(".", ",")} €`,
                },
              ]);
            } else if (parsed.delta) {
              setMessages((m) => {
                const next = [...m];
                next[next.length - 1] = {
                  role: "assistant",
                  text: next[next.length - 1].text + parsed.delta,
                };
                return next;
              });
            }
          } catch {
            /* chunk incomplet */
          }
        }
      }
    } catch {
      setMessages((m) => {
        const next = [...m];
        next[next.length - 1] = {
          role: "assistant",
          text: `Désolé, une erreur est survenue. Contactez-nous directement au ${contact.phone_display}.`,
        };
        return next;
      });
    } finally {
      // Détection du transfert vers un humain
      setMessages((m) => {
        const next = [...m];
        const last = next[next.length - 1];
        if (last?.role === "assistant" && last.text.includes("[HANDOVER]")) {
          next[next.length - 1] = {
            ...last,
            text: last.text.replace("[HANDOVER]", "").trim(),
            handover: true,
          };
        }
        return next;
      });
      setLoading(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen((v) => !v)}
        aria-label={open ? "Fermer l'assistant" : "Ouvrir l'assistant"}
        data-testid="chat-toggle-button"
        className="fixed bottom-[5.5rem] right-6 z-50 flex h-12 w-12 items-center justify-center rounded-full border border-gold/40 bg-coal/90 text-gold shadow-lg shadow-black/40 backdrop-blur-xl transition-transform duration-300 hover:scale-105"
      >
        {open ? <X className="h-5 w-5" strokeWidth={1.5} /> : <MessageSquare className="h-5 w-5" strokeWidth={1.5} />}
      </button>

      {open && (
        <div
          data-testid="chat-panel"
          className="fixed bottom-[9.5rem] right-6 z-50 flex h-[460px] w-[calc(100vw-3rem)] max-w-sm flex-col overflow-hidden rounded-lg border border-white/10 bg-coal/95 shadow-2xl backdrop-blur-xl"
        >
          <div className="border-b border-white/10 px-5 py-4">
            <div className="flex items-center justify-between gap-3">
              <p className="text-sm font-semibold tracking-wide text-ivory">Assistant Univers Maasaï</p>
              {service && !showChoices && (
                <button
                  onClick={() => setShowChoices(true)}
                  className="text-[10px] font-semibold uppercase tracking-[0.12em] text-ivory/40 transition-colors hover:text-gold"
                  data-testid="chat-change-service"
                >
                  Changer de service
                </button>
              )}
            </div>
            <p className="text-xs text-ivory/40" data-testid="chat-current-agent">
              {currentAgent} — répond 24h/24
            </p>
          </div>

          <div className="flex-1 space-y-3 overflow-y-auto px-4 py-4" data-testid="chat-messages">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[85%] rounded-lg px-3.5 py-2.5 text-sm leading-relaxed ${
                    m.role === "user"
                      ? "bg-copper text-ivory"
                      : "border border-white/10 bg-white/5 text-ivory/85"
                  }`}
                >
                  {m.role === "assistant" && m.agent_name && (
                    <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-[0.15em] text-gold/70" data-testid={`chat-agent-badge-${i}`}>
                      {m.agent_name}
                    </p>
                  )}
                  {m.text || (loading && i === messages.length - 1 && !m.link ? "…" : "")}
                  {m.link && (
                    <a
                      href={m.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-primary mt-3 !h-10 !px-5 !text-[11px]"
                      data-testid={`chat-payment-link-${i}`}
                    >
                      {m.linkLabel}
                    </a>
                  )}
                  {m.handover && (
                    <div className="mt-3 space-y-2 border-t border-white/10 pt-3" data-testid={`chat-handover-${i}`}>
                      <p className="text-[10px] font-semibold uppercase tracking-[0.15em] text-gold/70">
                        Contacter l'équipe
                      </p>
                      <div className="flex flex-wrap gap-2">
                        <a
                          href={`https://wa.me/${contact.whatsapp}?text=${encodeURIComponent("Bonjour, je souhaite échanger avec l'équipe.")}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1.5 rounded-md border border-[#25D366]/40 px-2.5 py-1.5 text-[11px] text-[#25D366] transition-colors hover:bg-[#25D366]/10"
                          data-testid={`chat-handover-whatsapp-${i}`}
                        >
                          <MessageSquare className="h-3 w-3" strokeWidth={1.5} />
                          WhatsApp
                        </a>
                        <Link
                          to="/contact"
                          onClick={() => setOpen(false)}
                          className="flex items-center gap-1.5 rounded-md border border-gold/40 px-2.5 py-1.5 text-[11px] text-gold transition-colors hover:bg-gold/10"
                          data-testid={`chat-handover-contact-${i}`}
                        >
                          <FileText className="h-3 w-3" strokeWidth={1.5} />
                          Formulaire
                        </Link>
                        <a
                          href={`mailto:${contact.email}`}
                          className="flex items-center gap-1.5 rounded-md border border-white/20 px-2.5 py-1.5 text-[11px] text-ivory/70 transition-colors hover:bg-white/5"
                          data-testid={`chat-handover-email-${i}`}
                        >
                          <Mail className="h-3 w-3" strokeWidth={1.5} />
                          Email
                        </a>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
            {showChoices && (
              <div className="space-y-2 rounded-lg border border-white/10 bg-white/[0.03] p-3" data-testid="chat-service-choices">
                <p className="text-[10px] font-semibold uppercase tracking-[0.15em] text-gold/70">
                  Quel service souhaitez-vous contacter ?
                </p>
                {SERVICE_CHOICES.map((choice) => (
                  <button
                    key={choice.key}
                    onClick={() => chooseService(choice)}
                    className="w-full rounded-md border border-white/10 px-3 py-2.5 text-left transition-colors hover:border-copper/60 hover:bg-copper/10"
                    data-testid={`chat-service-${choice.key}`}
                  >
                    <span className="block text-sm font-medium text-ivory">{choice.label}</span>
                    <span className="block text-[11px] text-ivory/45">{choice.sub}</span>
                  </button>
                ))}
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          <form onSubmit={send} className="border-t border-white/10 p-3">
            <div className="flex gap-2">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Votre question…"
                className="input-dark !h-10 flex-1 !text-[13px]"
                data-testid="chat-input"
              />
              <button
                type="submit"
                disabled={loading || !input.trim()}
                aria-label="Envoyer"
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-copper text-ivory transition-colors hover:bg-copperdark disabled:opacity-40"
                data-testid="chat-send-button"
              >
                <Send className="h-4 w-4" strokeWidth={1.5} />
              </button>
            </div>
            <label className="mt-2 flex cursor-pointer items-center gap-2 text-[10px] text-ivory/35" data-testid="chat-memory-toggle">
              <input
                type="checkbox"
                checked={memory}
                onChange={(e) => setMemory(e.target.checked)}
                className="h-3 w-3 accent-[#C4704F]"
                data-testid="chat-memory-checkbox"
              />
              Mémoriser la conversation pour améliorer le service
            </label>
          </form>
        </div>
      )}
    </>
  );
}
